import { useCreateSummaryTemplateMutation } from '@/types/vue-apollo-helpers';
import { useToast } from 'primevue/usetoast';
import { useFormErrors } from '@/composables/useFormErrors';
import {
  EXTRA_OPTIONS,
  OUTCOME_OPTIONS,
  PRESET_CONFIGS,
  SCOPE_DETAILS,
  SCOPE_ORDER,
  buildCreateSummaryInput,
  buildSummaryColumnDrafts,
  buildSummaryPreviewRows,
  type ClassificationCategory,
  type DegreeClassVisibility,
  type GraduatingStatusLabels,
  type LegendConfig,
  type ProgrammePreset,
  type ReportMode,
  type SummaryCalculationMethod,
  type SummaryExtraKey,
  type SummaryHeaderStyle,
  type SummaryOutcomeKey,
  type SummaryScope,
  type SummaryWizardAnswers,
} from '@/utils/summaryWizard';

export type SummaryWizardStepId =
  | 'name'
  | 'preset'
  | 'method'
  | 'reportMode'
  | 'scopes'
  | 'headerStyle'
  | 'stats'
  | 'outcomes'
  | 'extras'
  | 'classification'
  | 'displayOptions'
  | 'review';

interface TranscriptEntry {
  stepId: SummaryWizardStepId;
  question: string;
  answer: string;
}

export function useSummaryWizard(emit: (event: 'close' | 'saved') => void) {
  const toast = useToast();
  const { serverErrors, handleServerErrors, clearErrors } = useFormErrors();

  const { mutate: createSummaryTemplate } = useCreateSummaryTemplateMutation();

  const answers = reactive<SummaryWizardAnswers>({
    name: '',
    calculationMethod: 'cgpa',
    headerStyle: 'grouped',
    scopes: ['previous', 'current', 'cumulative'],
    fullStats: true,
    outcomes: ['outstanding_count', 'failed_courses', 'degree_class', 'status'],
    extras: ['grade_summary', 'failed_courses_list', 'legend'],
    includeCampus: false,
    showSignatures: true,
    signatureTitles: [],
    graduationConditions: {
      checkOutstandingCourses: true,
      checkMinUnits: true,
      checkFinalYear: true,
    },
    pdfHeader: {
      schoolName: '',
      reportTitle: '',
      semesterFormat: 'number' as const,
      logoPosition: 'left' as const,
      levelDisplayMap: {},
    },
    programmePreset: 'undergraduate' as ProgrammePreset,
    reportMode: 'semester' as ReportMode,
    degreeClassVisibility: 'auto' as DegreeClassVisibility,
    graduatingCategories: PRESET_CONFIGS.undergraduate.graduatingCategories,
    nonGraduatingStatuses: PRESET_CONFIGS.undergraduate.nonGraduatingStatuses,
    graduatingStatusLabels: { ...PRESET_CONFIGS.undergraduate.graduatingStatusLabels },
    legend: {
      showClassPerformance: true,
      showInterpretation: true,
      interpretationLines: [],
    },
  });

  const transcript = ref<TranscriptEntry[]>([]);
  const saving = ref(false);

  // Per-step scratch state
  const nameInput = ref('');
  const selectedScopes = ref<SummaryScope[]>(['previous', 'current', 'cumulative']);
  const selectedOutcomes = ref<SummaryOutcomeKey[]>(['outstanding_count', 'failed_courses', 'degree_class', 'status']);
  const selectedExtras = ref<SummaryExtraKey[]>(['grade_summary', 'failed_courses_list', 'legend']);

  // --- Step machine ---------------------------------------------------------

  const stepSequence: SummaryWizardStepId[] = [
    'name', 'preset', 'method', 'reportMode', 'scopes', 'headerStyle', 'stats', 'outcomes', 'extras', 'classification', 'displayOptions', 'review',
  ];

  const answeredSteps = computed(() => new Set(transcript.value.map((t) => t.stepId)));

  const currentStep = computed<SummaryWizardStepId>(() => {
    const next = stepSequence.find((step) => !answeredSteps.value.has(step));
    return next || 'review';
  });

  const progress = computed(() => ({
    answered: transcript.value.length,
    total: stepSequence.length - 1,
  }));

  const currentQuestion = computed<string>(() => {
    switch (currentStep.value) {
    case 'name':
      return 'Hi! 👋 Let\'s build a summary sheet together — the end-of-semester report showing each student\'s '
          + 'GPA, CGPA and academic standing. What should we call it?';
    case 'preset':
      return 'What type of programme is this template for?';
    case 'method':
      return 'How does your institution compute performance?';
    case 'reportMode':
      return 'What type of report should this template generate?';
    case 'scopes':
      return 'Which semester blocks should the sheet show?';
    case 'headerStyle':
      return 'How should the column headers look?';
    case 'stats':
      return 'Show detailed breakdown (TNUR, TNUP, TCP + GPA/CGPA) or just the averages (GPA/CGPA only)?';
    case 'outcomes':
      return 'Which outcome columns should close the sheet?';
    case 'extras':
      return 'Anything extra below the table?';
    case 'classification':
      return 'Review and customise the classification categories and status labels.';
    case 'displayOptions':
      return 'Almost done! Configure signatures and graduation conditions for the PDF.';
    case 'review':
      return 'That\'s everything! Check the sample sheet, then create it when you\'re happy.';
    default:
      return '';
    }
  });

  function pushAnswer(stepId: SummaryWizardStepId, answer: string) {
    transcript.value.push({ stepId, question: currentQuestion.value, answer });
  }

  function goBack() {
    if (transcript.value.length === 0 || saving.value) return;
    const last = transcript.value.pop();
    if (last?.stepId === 'name') nameInput.value = answers.name;
    clearErrors();
  }

  function jumpBackTo(index: number) {
    if (saving.value) return;
    const target = transcript.value[index];
    if (!target) return;
    transcript.value.splice(index);
    if (target.stepId === 'name') nameInput.value = answers.name;
    clearErrors();
  }

  // --- Step answer handlers --------------------------------------------------

  function answerName() {
    const name = nameInput.value.trim();
    if (!name) return;
    answers.name = name;
    pushAnswer('name', `"${name}"`);
  }

  const methodOptions: Array<{ value: SummaryCalculationMethod; label: string; description: string }> = [
    {
      value: 'cgpa',
      label: 'CGPA (grade points)',
      description: 'GPA = credit points ÷ units on a 5-point scale — the standard university system',
    },
    {
      value: 'weighted',
      label: 'Weighted (score × units)',
      description: 'Raw scores × credit units; degree classes use percentage thresholds (First Class = 70+)',
    },
  ];

  function answerMethod(method: SummaryCalculationMethod) {
    answers.calculationMethod = method;
    const option = methodOptions.find((m) => m.value === method);
    pushAnswer('method', option?.label || method);
  }

  const scopeOptions = SCOPE_ORDER.map((scope) => ({ value: scope, label: SCOPE_DETAILS[scope].label }));

  function toggleScope(scope: SummaryScope) {
    const index = selectedScopes.value.indexOf(scope);
    if (index >= 0) selectedScopes.value.splice(index, 1);
    else selectedScopes.value.push(scope);
  }

  function confirmScopes() {
    if (selectedScopes.value.length === 0) return;
    answers.scopes = SCOPE_ORDER.filter((s) => selectedScopes.value.includes(s));
    pushAnswer('scopes', answers.scopes.map((s) => SCOPE_DETAILS[s].group).join(', '));
  }

  // Phase 1: Header style
  const headerStyleOptions: Array<{ value: SummaryHeaderStyle; label: string; description: string }> = [
    { value: 'grouped', label: 'Grouped (spanning headers)', description: 'Columns grouped under Previous / Current / Cumulative headers' },
    { value: 'flat', label: 'Flat (prefixed labels)', description: 'Each column gets a prefix like "Prev CTNUR", "Curr TCP"' },
  ];

  function answerHeaderStyle(style: SummaryHeaderStyle) {
    answers.headerStyle = style;
    const option = headerStyleOptions.find((o) => o.value === style);
    pushAnswer('headerStyle', option?.label || style);
  }

  function answerStats(full: boolean) {
    answers.fullStats = full;
    pushAnswer('stats', full ? 'Full working (TCP, TNUP, TNUR + averages)' : 'Just the GPA/CGPA');
  }

  function toggleOutcome(key: SummaryOutcomeKey) {
    const index = selectedOutcomes.value.indexOf(key);
    if (index >= 0) selectedOutcomes.value.splice(index, 1);
    else selectedOutcomes.value.push(key);
  }

  function confirmOutcomes() {
    answers.outcomes = OUTCOME_OPTIONS.filter((o) => selectedOutcomes.value.includes(o.key)).map((o) => o.key);
    pushAnswer(
      'outcomes',
      answers.outcomes.length > 0
        ? OUTCOME_OPTIONS.filter((o) => answers.outcomes.includes(o.key)).map((o) => o.label).join(', ')
        : 'No outcome columns',
    );
  }

  function toggleExtra(key: SummaryExtraKey) {
    const index = selectedExtras.value.indexOf(key);
    if (index >= 0) selectedExtras.value.splice(index, 1);
    else selectedExtras.value.push(key);
  }

  function confirmExtras() {
    answers.extras = EXTRA_OPTIONS.filter((e) => selectedExtras.value.includes(e.key)).map((e) => e.key);
    pushAnswer(
      'extras',
      answers.extras.length > 0
        ? EXTRA_OPTIONS.filter((e) => answers.extras.includes(e.key)).map((e) => e.label).join(', ')
        : 'Nothing extra',
    );
  }

  function answerPreset(preset: ProgrammePreset) {
    answers.programmePreset = preset;
    if (preset !== 'custom') {
      const config = PRESET_CONFIGS[preset];
      answers.calculationMethod = config.calculationMethod;
      answers.graduatingCategories = [...config.graduatingCategories];
      answers.nonGraduatingStatuses = [...config.nonGraduatingStatuses];
      answers.graduatingStatusLabels = { ...config.graduatingStatusLabels };
    }
    transcript.value.push({ stepId: 'preset', question: 'Programme type', answer: preset === 'custom' ? 'Custom' : PRESET_CONFIGS[preset].label });
  }

  function answerReportMode(mode: ReportMode) {
    answers.reportMode = mode;
    if (mode === 'final_result' && !answers.scopes.includes('cumulative')) {
      answers.scopes = ['previous', 'current', 'cumulative'];
    }
    transcript.value.push({ stepId: 'reportMode', question: 'Report mode', answer: mode === 'semester' ? 'Semester' : 'Final Result' });
  }

  function confirmClassification() {
    transcript.value.push({ stepId: 'classification', question: 'Classification', answer: `${answers.graduatingCategories.length} graduating, ${answers.nonGraduatingStatuses.length} non-graduating categories` });
  }

  // Phase 5+7: Display options (signatures + graduation conditions)
  const displayOpts = reactive({
    includeCampus: false,
    showSignatures: true,
    signatureInput: '',
    signatureTitles: ['Head of Department', 'Dean', 'External Examiner'] as string[],
    checkOutstandingCourses: true,
    checkMinUnits: true,
    checkFinalYear: true,
    pdfSchoolName: '',
    pdfReportTitle: '',
    pdfSemesterFormat: 'number' as 'full' | 'number',
    pdfLogoPosition: 'left' as 'left' | 'right',
    pdfLevelMapInput: '',
    pdfLevelDisplayMap: {} as Record<string, string>,
  });

  function addSignatureTitle() {
    const title = displayOpts.signatureInput.trim();
    if (title && !displayOpts.signatureTitles.includes(title)) {
      displayOpts.signatureTitles.push(title);
    }
    displayOpts.signatureInput = '';
  }

  function removeSignatureTitle(index: number) {
    displayOpts.signatureTitles.splice(index, 1);
  }

  function addLevelMapping() {
    const raw = (displayOpts.pdfLevelMapInput || '').trim();
    const [level, display] = raw.split(':').map((s) => s.trim());
    if (level && display) {
      displayOpts.pdfLevelDisplayMap[level] = display;
      displayOpts.pdfLevelMapInput = '';
    }
  }

  function confirmDisplayOptions() {
    answers.includeCampus = displayOpts.includeCampus;
    answers.showSignatures = displayOpts.showSignatures;
    answers.signatureTitles = [...displayOpts.signatureTitles];
    answers.pdfHeader = {
      schoolName: displayOpts.pdfSchoolName,
      reportTitle: displayOpts.pdfReportTitle,
      semesterFormat: displayOpts.pdfSemesterFormat,
      logoPosition: displayOpts.pdfLogoPosition,
      levelDisplayMap: { ...displayOpts.pdfLevelDisplayMap },
    };
    answers.graduationConditions = {
      checkOutstandingCourses: displayOpts.checkOutstandingCourses,
      checkMinUnits: displayOpts.checkMinUnits,
      checkFinalYear: displayOpts.checkFinalYear,
    };
    const parts: string[] = [];
    if (displayOpts.showSignatures) parts.push(`Signatures: ${displayOpts.signatureTitles.join(', ') || 'default'}`);
    else parts.push('No signatures');
    const conditions: string[] = [];
    if (displayOpts.checkOutstandingCourses) conditions.push('outstanding courses');
    if (displayOpts.checkMinUnits) conditions.push('min units');
    if (displayOpts.checkFinalYear) conditions.push('final year');
    parts.push(conditions.length === 3 ? 'All graduation checks' : `Graduation: ${conditions.join(', ') || 'none'}`);
    pushAnswer('displayOptions', parts.join(' · '));
  }

  // --- Preview ---------------------------------------------------------------

  /** Only answered steps appear, so the sample sheet builds up step by step. */
  const previewAnswers = computed<SummaryWizardAnswers>(() => {
    const done = answeredSteps.value;
    return {
      ...answers,
      headerStyle: done.has('headerStyle') ? answers.headerStyle : 'grouped',
      scopes: done.has('scopes') ? answers.scopes : [],
      fullStats: done.has('stats') ? answers.fullStats : false,
      outcomes: done.has('outcomes') ? answers.outcomes : [],
      extras: done.has('extras') ? answers.extras : [],
    };
  });

  const previewColumns = computed(() => buildSummaryColumnDrafts(previewAnswers.value));
  const previewRows = computed(() => buildSummaryPreviewRows(previewAnswers.value));
  const reviewColumns = computed(() => buildSummaryColumnDrafts(answers));

  // --- Save -------------------------------------------------------------------

  async function saveTemplate() {
    if (saving.value) return;
    saving.value = true;
    clearErrors();
    try {
      const input = buildCreateSummaryInput(answers);
      await createSummaryTemplate({ input });
      toast.add({
        severity: 'success',
        summary: 'Summary template created',
        detail: `"${answers.name}" is ready — generate it for a class or programme right from this tab.`,
        life: 4000,
      });
      emit('saved');
    } catch (e) {
      handleServerErrors(e);
      const error = e as Error;
      toast.add({
        severity: 'error',
        summary: 'Error',
        detail: error.message || 'Failed to create summary template',
        life: 4000,
      });
    } finally {
      saving.value = false;
    }
  }

  return {
    // state
    answers,
    transcript,
    saving,
    nameInput,
    selectedScopes,
    selectedOutcomes,
    selectedExtras,

    // machine
    currentStep,
    currentQuestion,
    progress,

    // data
    methodOptions,
    scopeOptions,
    headerStyleOptions,
    outcomeOptions: OUTCOME_OPTIONS,
    extraOptions: EXTRA_OPTIONS,
    serverErrors,

    // display options
    displayOpts,
    addSignatureTitle,
    removeSignatureTitle,
    addLevelMapping,

    // preview / review
    previewAnswers,
    previewColumns,
    previewRows,
    reviewColumns,

    // handlers
    answerName,
    answerPreset,
    answerMethod,
    answerReportMode,
    confirmClassification,
    PRESET_CONFIGS,
    toggleScope,
    confirmScopes,
    answerHeaderStyle,
    answerStats,
    toggleOutcome,
    confirmOutcomes,
    toggleExtra,
    confirmExtras,
    confirmDisplayOptions,
    goBack,
    jumpBackTo,
    saveTemplate,
  };
}
